/**
 * Calculate Levenshtein distance between two strings
 * Returns the number of single-character edits (insertions, deletions, substitutions)
 * needed to change one string into another
 */
export function calculateEditDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create a 2D array for dynamic programming
  const dp: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Initialize first column and row
  for (let i = 0; i <= len1; i++) {
    dp[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    dp[0][j] = j;
  }

  // Fill the dp table
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // deletion
          dp[i][j - 1] + 1, // insertion
          dp[i - 1][j - 1] + 1 // substitution
        );
      }
    }
  }

  return dp[len1][len2];
}

/**
 * Calculate edit distance as a percentage of the original text length
 * Returns a value between 0 (no changes) and 1+ (extensive changes)
 */
export function calculateEditDistancePercentage(
  original: string,
  edited: string
): number {
  const distance = calculateEditDistance(original, edited);
  const maxLength = Math.max(original.length, edited.length);

  if (maxLength === 0) return 0;

  return distance / maxLength;
}

/**
 * Determine if text was edited based on edit distance
 * Returns true if changes exceed a threshold (default 1%)
 */
export function wasTextEdited(
  original: string,
  current: string,
  threshold = 0.01
): boolean {
  const percentage = calculateEditDistancePercentage(original, current);
  return percentage > threshold;
}
